import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Form, Field, reduxForm } from 'redux-form';

import cc from 'card-validator';
import {
    requiredValidation,
    validateCCNumber,
    validateCCExp,
    validateCCCvv,
} from 'ggApp/modules/form/validations';
import {
    CREDIT_CARD_MASK_SYMBOL,
    DEFAULT_CARD_MASKS,
    AMEX_CARD_MASKS,
    PAYMENT_CARD_INPUT_IDS,
} from 'ggApp/modules/payment/constants';
import { reduxForm as reduxFormSelector } from 'ggApp/modules/form/selectors';
import { COMMON_FORM_NAME } from 'ggApp/modules/adyen/constants';
import FormFieldWrap from 'ggApp/shared/components/form/fieldWrap';
import { getTranslation } from 'ggApp/utils/text';

import { Button } from '@getgrover/ui';

import styles from './index.module.scss';

const enhance = compose(
    reduxForm({
        getFormState: reduxFormSelector,
        form: COMMON_FORM_NAME,
        validate: (values) => {
            const errors = {};

            errors.cardNumber = validateCCNumber({ number: values.cardNumber });
            errors.holderName = requiredValidation(values.holderName);
            errors.expiry = validateCCExp({ exp: values.expiry });
            errors.cvc = validateCCCvv({ cvv: values.cvc });

            return errors;
        },
    }),
);

class AdyenFormCreditCard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isAmex: false,
            ...DEFAULT_CARD_MASKS,
        };
        this.submit = this.submit.bind(this);
        this.setCardInputMasks = this.setCardInputMasks.bind(this);
    }

    setCardInputMasks(event) {
        const { change } = this.props;

        const rawCardNumber = event.target.value;
        const cardNumber = rawCardNumber
            .split(CREDIT_CARD_MASK_SYMBOL)
            .filter((s) => Boolean(s))
            .join('');
        const { card } = cc.number(cardNumber);

        if (card) {
            const cardMasks = card.isAmex ? AMEX_CARD_MASKS : DEFAULT_CARD_MASKS;

            this.setState((prevState) => {
                if (prevState.isAmex !== card.isAmex) {
                    change('cvc', '');
                }

                return {
                    isAmex: card.isAmex,
                    ...cardMasks,
                };
            });
        }
    }

    submit(values) {
        const { callback } = this.props;

        if (callback && typeof callback === 'function') {
            callback(values);
        }
    }

    // eslint-disable-next-line class-methods-use-this
    isFormValid(formErrors) {
        return Object.keys(formErrors).filter((key) => formErrors[key]).length === 0;
    }

    render() {
        const { loading, setFocusField, handleSubmit, noButton } = this.props;
        const { store } = this.context;
        const { CvcMask, cardNumberMask } = this.state;

        return (
            <Form
                id={COMMON_FORM_NAME}
                className={styles.AdyenFormCreditCard}
                onSubmit={handleSubmit(this.submit)}
            >
                <div>
                    <div className={styles.AdyenFormCreditCard__form}>
                        <div>
                            <Field
                                id={PAYMENT_CARD_INPUT_IDS.CARD_NUMBER}
                                label={getTranslation(store, 'FORM_LABEL_CC_NUMBER')}
                                name="cardNumber"
                                type="text"
                                onFocus={() => setFocusField('number')}
                                onChange={this.setCardInputMasks}
                                maskChar={CREDIT_CARD_MASK_SYMBOL}
                                mask={cardNumberMask}
                                component={FormFieldWrap}
                            />
                        </div>
                        <div>
                            <Field
                                id={PAYMENT_CARD_INPUT_IDS.CARD_HOLDER}
                                label={getTranslation(store, 'FORM_LABEL_CC_HOLDER_NAME')}
                                name="holderName"
                                type="text"
                                onFocus={() => setFocusField('name')}
                                component={FormFieldWrap}
                            />
                        </div>
                        <div className={styles.AdyenFormCreditCard__split}>
                            <div className={styles.AdyenFormCreditCard__splitBox}>
                                <Field
                                    id={PAYMENT_CARD_INPUT_IDS.EXPIRY}
                                    label={getTranslation(store, 'FORM_LABEL_CC_VALID_THRU')}
                                    name="expiry"
                                    type="text"
                                    onFocus={() => setFocusField('expiry')}
                                    mask="99 / 99"
                                    maskChar={CREDIT_CARD_MASK_SYMBOL}
                                    component={FormFieldWrap}
                                />
                            </div>
                            <div className={styles.AdyenFormCreditCard__splitBox}>
                                <Field
                                    id={PAYMENT_CARD_INPUT_IDS.CVV}
                                    label={getTranslation(store, 'FORM_LABEL_CC_CVC')}
                                    name="cvc"
                                    type="text"
                                    onFocus={() => setFocusField('cvc')}
                                    mask={CvcMask}
                                    maskChar={CREDIT_CARD_MASK_SYMBOL}
                                    component={FormFieldWrap}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {!noButton && (
                    <div className={styles.AdyenFormCreditCard__button}>
                        <Button primary fullWidth loading={loading} onClick={this.onSubmit}>
                            Submit credit card
                        </Button>
                    </div>
                )}
            </Form>
        );
    }
}

AdyenFormCreditCard.contextTypes = {
    store: PropTypes.shape({}),
};

AdyenFormCreditCard.propTypes = {
    loading: PropTypes.bool,
    noButton: PropTypes.bool,
    setFocusField: PropTypes.func,
    handleSubmit: PropTypes.func,
    change: PropTypes.func,
    callback: PropTypes.func,
};

AdyenFormCreditCard.defaultProps = {
    loading: false,
    noButton: false,
    setFocusField: () => {},
    handleSubmit: () => {},
    change: () => {},
    callback: () => {},
};

export default enhance(AdyenFormCreditCard);
